'use es6';
'use client';

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { ReactStore } from "../../../../internal/@base-ui/utils/store";
import { Timeout } from "../../../../internal/@base-ui/utils/useTimeout";
import { NOOP } from "../../../../internal/@base-ui/utils/empty";
import { REASONS } from "../../internals/reasons.js";
import { NullStore } from "../../utils/NullStore.js";
import { attachPreventUnmountOnClose, createInitialPopupStoreState, popupStoreSelectors, PopupTriggerMap, createPopupOpenState } from "../../utils/popups/index.js";
import { PATIENT_CLICK_THRESHOLD } from "../../internals/constants.js";
const selectors = Object.assign({}, popupStoreSelectors, {
  disabled: state => state.disabled,
  // `trigger-change` describes a popup moving between triggers, which only has
  // meaning while it is open. Dropping it once closed keeps a late or stale
  // restoration from marking a closing popup instant and skipping its exit
  // transition, including on close paths that never reach `setOpen` — a
  // controlled consumer committing `open={false}` goes straight through the prop.
  instantType: state => state.instantType === 'trigger-change' && !popupStoreSelectors.open(state) ? undefined : state.instantType,
  openMethod: state => state.openMethod,
  openChangeReason: state => state.openChangeReason,
  isPressOpenedByTrigger: (state, triggerId) => state.openChangeReason === REASONS.triggerPress && popupStoreSelectors.isOpenedByTrigger(state, triggerId),
  // The trigger selectors below resolve to a constant for triggers that don't use them, so state
  // changes don't re-render every inactive trigger.
  isTouchPressOpen: (state, openOnHover) => openOnHover && state.openMethod === 'touch' && state.openChangeReason === REASONS.triggerPress,
  hasTriggerFocusGuards: (state, triggerId) => popupStoreSelectors.isOpenedByTrigger(state, triggerId) && !state.focusManagerModal,
  modal: state => state.modal,
  titleElementId: state => state.titleElementId,
  descriptionElementId: state => state.descriptionElementId,
  openOnHover: state => state.openOnHover,
  closeDelay: state => state.closeDelay,
  adaptiveOrigin: state => state.adaptiveOrigin
});

/**
 * The store view that detached handle-backed triggers read from. Both the real `PopoverStore` and
 * the inert fallback store satisfy it, so a trigger can read from whichever store the handle
 * currently exposes. Narrowed to the members a trigger actually uses — the trigger-data members plus
 * `setOpen` (called by the focus guards) — so the exposed surface can't bypass the open-change
 * pipeline; on the detached fallback store every one of these mutations is a no-op.
 */

export class PopoverStore extends ReactStore {
  constructor(initialState, floatingId, nested) {
    const triggerElements = new PopupTriggerMap();
    super(createInitialState(initialState, triggerElements, floatingId, nested), createInitialContext(triggerElements), selectors);
    this.setOpen = (nextOpen, eventDetails) => {
      var _this$context$onOpenC, _this$context;
      const isHover = eventDetails.reason === REASONS.triggerHover;
      const isKeyboardClick = eventDetails.reason === REASONS.triggerPress && eventDetails.event.detail === 0;
      const isDismissClose = !nextOpen && (eventDetails.reason === REASONS.escapeKey || eventDetails.reason == null);
      const shouldPreventUnmountOnClose = attachPreventUnmountOnClose(eventDetails);
      const activeTriggerId = this.select('activeTriggerId');
      if (!nextOpen && eventDetails.reason === REASONS.closePress && eventDetails.trigger == null && activeTriggerId != null) {
        var _ref, _this$context$trigger;
        eventDetails.trigger = (_ref = (_this$context$trigger = this.context.triggerElements.getById(activeTriggerId)) !== null && _this$context$trigger !== void 0 ? _this$context$trigger : this.select('activeTriggerElement')) !== null && _ref !== void 0 ? _ref : undefined;
      }
      (_this$context$onOpenC = (_this$context = this.context).onOpenChange) === null || _this$context$onOpenC === void 0 || _this$context$onOpenC.call(_this$context, nextOpen, eventDetails);
      if (eventDetails.isCanceled) {
        return;
      }
      this.state.floatingRootContext.dispatchOpenChange(nextOpen, eventDetails);
      const changeState = () => {
        const popupOpenState = createPopupOpenState(this.state, nextOpen, eventDetails.trigger, shouldPreventUnmountOnClose());
        popupOpenState.openChangeReason = eventDetails.reason;
        this.update(popupOpenState);
      };
      if (isHover) {
        // Only allow "patient" clicks to close the popover if it's open.
        // If they clicked within 500ms of the popover opening, keep it open.
        this.context.stickIfOpen = true;
        this.context.stickIfOpenTimeout.start(PATIENT_CLICK_THRESHOLD, () => {
          this.context.stickIfOpen = false;
        });
        ReactDOM.flushSync(changeState);
      } else {
        changeState();
      }
      let instantType;
      if (isKeyboardClick) {
        instantType = 'click';
      } else if (isDismissClose) {
        instantType = 'dismiss';
      } else if (eventDetails.reason === REASONS.focusOut) {
        instantType = 'focus';
      }
      this.set('instantType', instantType);
    };
  }
}

/**
 * Creates the inert fallback store used by detached handle-backed triggers while no
 * `Popover.Root` is attached. It preserves a popover-specific trigger registry in context so
 * detached triggers can register before migrating to the live root store. `setOpen` is a no-op
 * (matching the inert reads/writes of `NullStore`), so a trigger can hand the store to focus-guard
 * helpers that expect `setOpen` without it ever taking effect while detached.
 */
export function createNullPopoverStore() {
  const triggerElements = new PopupTriggerMap();
  const store = new NullStore(Object.freeze(createInitialState(undefined, triggerElements)), Object.freeze(createInitialContext(triggerElements)), selectors);
  return Object.assign(store, {
    setOpen: NOOP
  });
}
function createInitialState(initialState, triggerElements, floatingId, nested = false) {
  const state = Object.assign({}, createInitialPopupStoreState(triggerElements, floatingId, nested), {
    disabled: false,
    modal: false,
    focusManagerModal: false,
    instantType: undefined,
    openMethod: null,
    openChangeReason: null,
    titleElementId: undefined,
    descriptionElementId: undefined,
    openOnHover: false,
    closeDelay: 0,
    adaptiveOrigin: undefined
  }, initialState);
  if (state.open && (initialState === null || initialState === void 0 ? void 0 : initialState.mounted) === undefined) {
    state.mounted = true;
  }
  return state;
}
function createInitialContext(triggerElements) {
  return {
    popupRef: /*#__PURE__*/React.createRef(),
    onOpenChange: undefined,
    onOpenChangeComplete: undefined,
    triggerFocusTargetRef: /*#__PURE__*/React.createRef(),
    beforeTriggerFocusGuardRef: /*#__PURE__*/React.createRef(),
    beforeContentFocusGuardRef: /*#__PURE__*/React.createRef(),
    stickIfOpenTimeout: new Timeout(),
    stickIfOpen: true,
    triggerElements
  };
}